<template>
  <div class="gauge-hud" @pointerdown.stop @touchstart.stop>
    <button
      v-if="!hudOpen"
      type="button"
      class="gauge-pill"
      @click="hudOpen = true"
    >
      Debug Gauges
    </button>
    <div v-else class="gauge-panel">
      <header class="gauge-head">
        <div class="gauge-title-row">
          <h3>Gauge HUD</h3>
          <button
            type="button"
            class="icon-btn"
            title="Collapse"
            aria-label="Collapse gauge HUD"
            @click="hudOpen = false"
          >
            <i class="fas fa-minus" aria-hidden="true"></i>
          </button>
        </div>
        <p class="gauge-round">
          Round {{ displayTick }} / {{ displayTotal }}
          <span class="play-status" :class="{ playing }">
            {{ playing ? "Playing" : "Paused" }}
          </span>
        </p>
        <div class="gauge-controls">
          <button
            type="button"
            class="ctrl-btn"
            :disabled="playing || displayTick <= 1"
            @click="emitInspect(displayTick - 1)"
          >
            &lt; Prev
          </button>
          <button
            type="button"
            class="ctrl-btn play-btn"
            @click="$emit('toggle-playing')"
          >
            {{ playing ? "Pause" : "Play" }}
          </button>
          <button
            type="button"
            class="ctrl-btn"
            :disabled="playing || displayTick >= displayTotal"
            @click="emitInspect(displayTick + 1)"
          >
            Next &gt;
          </button>
        </div>
        <input
          class="gauge-slider"
          type="range"
          min="1"
          :max="displayTotal"
          :value="displayTick"
          :disabled="playing || displayTotal < 1"
          @input="onSlider"
        />
      </header>

      <section v-if="heroRows.length" class="gauge-section">
        <h4>Heroes</h4>
        <ul>
          <li v-for="row in heroRows" :key="row.id" class="gauge-row hero">
            <div class="row-top">
              <span class="unit-name">
                <i class="fas fa-crown" aria-hidden="true"></i>
                {{ row.name }}
              </span>
              <span class="side-chip" :class="row.side">{{ sideLabel(row.side) }}</span>
            </div>
            <div class="row-meta">
              <span class="gauge-value">{{ row.gauge }} / 100</span>
              <span class="spd">SPD {{ row.spd }}</span>
              <span class="status-badge" :class="row.status">{{ statusLabel(row.status) }}</span>
            </div>
            <div class="bar-track" aria-hidden="true">
              <div class="bar-fill" :class="row.status" :style="{ width: barWidth(row.gauge) }"></div>
            </div>
          </li>
        </ul>
      </section>

      <section class="gauge-section">
        <h4>Troops</h4>
        <p v-if="!troopRows.length" class="empty-copy">No living troops.</p>
        <ul v-else>
          <li v-for="row in troopRows" :key="row.id" class="gauge-row">
            <div class="row-top">
              <span class="unit-name">{{ row.name }}</span>
              <span class="side-chip" :class="row.side">{{ sideLabel(row.side) }}</span>
            </div>
            <div class="row-meta">
              <span class="gauge-value">{{ row.gauge }} / 100</span>
              <span class="spd">SPD {{ row.spd }}</span>
              <span class="status-badge" :class="row.status">{{ statusLabel(row.status) }}</span>
            </div>
            <div class="bar-track" aria-hidden="true">
              <div class="bar-fill" :class="row.status" :style="{ width: barWidth(row.gauge) }"></div>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

const STATUS_LABEL = {
  acted: "ACTED",
  charging: "CHARGING",
  gridlocked: "GRIDLOCKED",
};

const props = defineProps({
  inspectTick: {
    type: Number,
    default: 0,
  },
  totalRounds: {
    type: Number,
    default: 0,
  },
  playing: {
    type: Boolean,
    default: false,
  },
  gauges: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["toggle-playing", "inspect"]);

const hudOpen = ref(false);

const displayTotal = computed(() =>
  Math.max(1, Math.floor(Number(props.totalRounds) || 0))
);
const displayTick = computed(() => {
  const tick = Math.floor(Number(props.inspectTick) || 0);
  if (tick < 1) return 1;
  return Math.min(displayTotal.value, tick);
});

const gaugeRows = computed(() => {
  const entries = Object.entries(props.gauges || {});
  return entries
    .map(([id, info]) => ({
      id,
      name: info?.name || "Unit",
      side: info?.side === "defender" ? "defender" : "attacker",
      isHero: !!info?.isHero,
      gauge: Math.max(0, Math.floor(Number(info?.gauge) || 0)),
      spd: Math.max(0, Math.floor(Number(info?.spd) || 0)),
      status: STATUS_LABEL[info?.status] ? info.status : "charging",
    }))
    .sort((a, b) => {
      if (a.side !== b.side) return a.side === "attacker" ? -1 : 1;
      return String(a.name).localeCompare(String(b.name)) || String(a.id).localeCompare(String(b.id));
    });
});

const heroRows = computed(() => gaugeRows.value.filter((row) => row.isHero));
const troopRows = computed(() => gaugeRows.value.filter((row) => !row.isHero));

function statusLabel(status) {
  return STATUS_LABEL[status] || "CHARGING";
}

function sideLabel(side) {
  return side === "defender" ? "DEF" : "ATK";
}

function barWidth(gauge) {
  const pct = Math.max(0, Math.min(100, Math.floor(Number(gauge) || 0)));
  return `${pct}%`;
}

function emitInspect(tick) {
  const next = Math.min(
    displayTotal.value,
    Math.max(1, Math.floor(Number(tick) || 1))
  );
  emit("inspect", next);
}

function onSlider(event) {
  emitInspect(event.target.value);
}
</script>

<style scoped>
.gauge-hud {
  position: absolute;
  top: 80px;
  right: 16px;
  z-index: 1050;
  pointer-events: auto;
  font-family: "Rajdhani", "Segoe UI", sans-serif;
  color: #d7e6ee;
}

.gauge-pill,
.ctrl-btn,
.icon-btn {
  cursor: pointer;
  color: #00f6ff;
  background: rgba(8, 14, 24, 0.82);
  border: 1px solid rgba(0, 246, 255, 0.55);
}

.gauge-pill {
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.gauge-panel {
  width: 280px;
  max-height: 380px;
  overflow-y: auto;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid cyan;
  background: rgba(4, 10, 20, 0.85);
  box-sizing: border-box;
}

.gauge-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.gauge-head h3,
.gauge-section h4 {
  margin: 0;
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
}

.gauge-head h3 {
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.icon-btn {
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 4px;
  font-size: 0.7rem;
}

.gauge-round {
  margin: 0.35rem 0 0.45rem;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.play-status {
  color: #c9a227;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.play-status.playing {
  color: #5dffb1;
}

.gauge-controls {
  display: flex;
  gap: 4px;
}

.ctrl-btn {
  flex: 1;
  padding: 0.28rem 0.2rem;
  border-radius: 4px;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.ctrl-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.play-btn {
  flex: 0.9;
}

.gauge-slider {
  width: 100%;
  margin: 0.5rem 0 0.15rem;
  accent-color: #00f6ff;
}

.gauge-section {
  margin-top: 0.7rem;
}

.gauge-section h4 {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
}

.gauge-section ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.empty-copy {
  margin: 0;
  font-size: 0.72rem;
  color: #88c0d0;
}

.gauge-row {
  padding: 0.28rem 0.1rem 0.15rem;
}

.row-top,
.row-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.unit-name {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unit-name i {
  color: #f0d060;
  font-size: 0.7rem;
}

.side-chip {
  flex-shrink: 0;
  font-size: 0.58rem;
  letter-spacing: 0.08em;
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  border: 1px solid rgba(0, 246, 255, 0.35);
}

.side-chip.attacker {
  color: #7ad7ff;
}

.side-chip.defender {
  color: #ffb4a2;
  border-color: rgba(255, 160, 130, 0.4);
}

.row-meta {
  margin-top: 0.12rem;
  font-size: 0.68rem;
  color: #9bb8c4;
}

.status-badge {
  font-size: 0.58rem;
  letter-spacing: 0.06em;
  padding: 0.05rem 0.28rem;
  border-radius: 3px;
  border: 1px solid currentColor;
}

.status-badge.acted {
  color: #5dffb1;
}

.status-badge.charging {
  color: #88c0d0;
}

.status-badge.gridlocked {
  color: #ffb347;
}

.bar-track {
  margin-top: 0.2rem;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: #00f6ff;
}

.bar-fill.acted {
  background: #5dffb1;
}

.bar-fill.gridlocked {
  background: #ffb347;
}
</style>
